import Ajv, {ErrorObject, Schema} from "ajv";
import addFormats from "ajv-formats";
import {JSONSchema7} from "json-schema";
import {Translator} from "@jsonforms/core";
import {Dispatch, ReactElement, SetStateAction} from "react";
import {Base} from "./base/Base";
import {Languages} from "./languages/Languages";
import {Degrees} from "./degrees/Degrees";
import {ModuleList} from "./modules/Modules";
import {FinalProject} from "./thesis/Thesis";
import {Comment} from "./comment/Comment";
import {Mapping} from "./mappings/Mappings";

import {TFnType} from "@tolgee/react";
import {I18nMessage} from "./Representation";

export const JSON_FORMS_CONFIG = {
    restrict: true,
    trim: false,
    showUnfocusedDescription: true,
    hideRequiredAsterisk: false,
}

export enum Level {OK, CONTEXT_FAILED, VALIDATION_FAILED, INCOMPLETE}

export type ReactState<T> = [T, Dispatch<SetStateAction<T>>];

export type AllStates = {
    base: ReactState<Base>;
    languages: ReactState<Languages>;
    degrees: ReactState<Degrees>;
    modules: ReactState<ModuleList[]>;
    mappings: ReactState<Mapping[]>;
    finalProject: ReactState<FinalProject>;
    comment: ReactState<Comment>;
    startTime: ReactState<number>;
    ordered: ReactState<any>[];
    showStepDialogs: ReactState<boolean[]>;
    stepsDialogsOpen: ReactState<boolean[]>;
};

export type TranslateFunction = TFnType;

export function asJsonformsErrorTranslate(t: TranslateFunction) {
    return (error: ErrorObject, _translate?: Translator) => {
        return t("ajv_error." + error.keyword, error.message, error.params);
    };
}

export function asJsonformsTranslate(t: TranslateFunction, namespace: string) {
    return ((key: string, defaultMessage: string | undefined = undefined, _context: any = undefined): string | undefined => {
        if (key.indexOf("error.") >= 0) {
            return defaultMessage;
        }
        return t(namespace + key, defaultMessage);
    }) as Translator;
}

export const NON_JSON_ERROR_PREFIX = "non_json$";

export abstract class StepForm {
    _schema: JSONSchema7;

    protected constructor(schema: JSONSchema7) {
        this._schema = schema;
    }

    abstract getTitleKey(): string;

    /**
     *
     * @param data
     * @param setData
     * @param allStates
     * @param t
     * @param lang
     * @param middleware This is needed if the JSON form uses the standard table formatting for arrays, which does not currently support I18N support for validation errors.
     */
    abstract getPage([data, setData]: ReactState<any>,  allStates: AllStates, t: TranslateFunction, lang: string): ReactElement;

    validate(newAjv: () => Ajv, data: any, _t: TranslateFunction, _allStates: AllStates): ErrorObject[] {
        const ajv = newAjv();
        ajv.validate(this._schema as Schema, data)
        return ajv.errors ?? [];
    }

    getFilteredValidationErrors(data: any, allStates: AllStates, t: TranslateFunction) {
        const ajvErrors = this.validate(() => {
            const ajv = new Ajv({
                allErrors: true,
                verbose: true,
                strict: false
            })
            addFormats(ajv);
            return ajv;
        }, data, t, allStates);

        return this.filterValidationErrors(data, ajvErrors);
    }

    getValidationMessageKeys(data: any, allStates: AllStates, t: TranslateFunction) {
        const errors = this.getFilteredValidationErrors(data, allStates, t);

        // may be empty after filtering
        if (errors === null || errors === undefined || errors.length === 0) {
            return null;
        }

        if (errors.some((errorObject) => errorObject.schemaPath.endsWith("/required"))) {
            return {
                level: Level.INCOMPLETE,
                messages: ["required_missing"]
            };
        } else {
            const messages = [];
            if (errors.some(errorObject => !errorObject.keyword.startsWith(NON_JSON_ERROR_PREFIX)))
                messages.push("step_invalid");

            errors.filter(errorObject => errorObject.keyword.startsWith(NON_JSON_ERROR_PREFIX))
                .map(errorObject => errorObject.message)
                .forEach(message => {if (message) messages.push(message)});

            return {
                level: Level.VALIDATION_FAILED,
                messages: messages
            };
        }
    }

    isValid(data: any, allStates: AllStates, t: TranslateFunction) {
        return this.getValidationMessageKeys(data, allStates, t) == null;
    }

    getStatus(data: any, allStates: AllStates, warningsOnly: boolean, t: TranslateFunction) {
        const validationMessageKeys = this.getValidationMessageKeys(data, allStates, t);
        if (validationMessageKeys != null) {
            return validationMessageKeys;
        }

        const contextMessages = this.getContextMessageKeys(data, allStates);
        if (contextMessages[0].length !== 0) {
            return {
                level: Level.CONTEXT_FAILED,
                messages: [...contextMessages[0], ...(warningsOnly ? [] : contextMessages[1])]
            };
        } else {
            return {
                level: Level.OK,
                messages: [...contextMessages[0], ...(warningsOnly ? [] : contextMessages[1])]
            };

        }
    }

    /**
     *
     * @param data
     * @param allStates
     * @return the first element in the result array is an array of error messages, the second is an array of info messages
     */
    abstract getContextMessageKeys(data: any, allStates: AllStates): [I18nMessage[], I18nMessage[]];

    filterValidationErrors(_data: any, errors: ErrorObject[] | null | undefined) {
        return errors;
    }

    abstract getExplanationMarkdownKey(): string;

    abstract getHelpUrl(): string;

    abstract getFormNs(): string | undefined;

    getSchema(): JSONSchema7 {
        return this._schema;
    }
}
